import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { dateTypes, dateRangeSchema, DateRangeFormData } from './date-range-form.types';

interface UseDateRangeFormProps {
  defaultValues?: DateRangeFormData;
  onApply: (values: DateRangeFormData) => void;
}

/**
 * Custom hook that manages the state and logic for the DateRangeForm component.
 *
 * Features:
 * - Manages dropdown and calendar open states
 * - Handles form state using react-hook-form with zod schema validation
 * - Provides handlers for preset selection and calendar date changes
 * - Automatically submits form when valid selections are made
 * - Supports both preset date ranges and custom date selections
 * - Handles partial date selections without triggering submission
 *
 * Behavior:
 * - Initializes with 'thisWeek' as default date type if no defaults provided
 * - Automatically closes dropdowns when valid selections are made
 * - Switches to 'custom' type when manual date range is selected
 * - Only triggers onApply callback when both start and end dates are valid
 * - Maintains UI state for both main dropdown and calendar popover
 *
 * @param defaultValues - Initial values for the date type and range (optional)
 * @param onApply - Callback function called when valid date selections are made
 * @param minDate - Optional minimum date to use for "all time" preset
 *
 * @returns {Object} Hook state and handlers
 * @returns {boolean} open - Main dropdown open state
 * @returns {function} setOpen - Function to control main dropdown state
 * @returns {boolean} calendarOpen - Calendar popover open state
 * @returns {string} dateType - Current selected date type (preset or 'custom')
 * @returns {DateRange} dateRange - Current selected date range
 * @returns {function} handleOptionClick - Handler for preset option selection
 * @returns {function} handleDateRangeSelect - Handler for calendar date selection
 * @returns {function} handleCalendarOpenChange - Handler for calendar popover state
 */
export function useDateRangeForm({
  defaultValues,
  onApply
}: UseDateRangeFormProps) {
  // UI state for dropdowns
  const [open, setOpen] = React.useState(false);
  const [calendarOpen, setCalendarOpen] = React.useState(false);

  // Local date calculation function
  const calculateDatesFromType = (dateType: string): { startDate?: Date; endDate?: Date } => {
    const now = new Date();
    let startDate: Date | undefined;
    let endDate: Date | undefined = now;

    switch (dateType) {
      case 'thisWeek': {
        // Get start of current week (Monday)
        const dayOfWeek = now.getDay();
        const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(now);
        startDate.setDate(now.getDate() - daysToSubtract);
        startDate.setHours(0, 0, 0, 0);
        break;
      }
      case 'lastWeek': {
        // Get last week (Monday to Sunday)
        const dayOfWeek = now.getDay();
        const daysToSubtract = dayOfWeek === 0 ? 13 : dayOfWeek + 6;
        startDate = new Date(now);
        startDate.setDate(now.getDate() - daysToSubtract);
        startDate.setHours(0, 0, 0, 0);

        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        break;
      }
      case 'lastMonth': {
        // Get last month
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        startDate.setHours(0, 0, 0, 0);

        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      }
      case 'monthToDate': {
        // Month to date (from start of current month)
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      }
      case 'lastQuarter': {
        // Get last quarter
        const currentQuarter = Math.floor(now.getMonth() / 3);
        const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
        startDate = new Date(now.getFullYear(), lastQuarter * 3, 1);
        startDate.setHours(0, 0, 0, 0);

        endDate = new Date(now.getFullYear(), (lastQuarter + 1) * 3, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      }
      case 'quarterToDate': {
        // Quarter to date
        const currentQuarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), currentQuarter * 3, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      }
      case 'last6Months': {
        // Last 6 months
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 6);
        startDate.setHours(0, 0, 0, 0);
        break;
      }
      case 'yearToDate': {
        // Year to date
        startDate = new Date(now.getFullYear(), 0, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      }
      case 'allTime': {
        // All time - return null values to indicate no date filtering
        startDate = undefined;
        endDate = undefined;
        break;
      }
      default: {
        // Default to this week
        const dayOfWeek = now.getDay();
        const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate = new Date(now);
        startDate.setDate(now.getDate() - daysToSubtract);
        startDate.setHours(0, 0, 0, 0);
      }
    }

    return { startDate, endDate };
  };

  // Initialize form with validation
  const form = useForm<DateRangeFormData>({
    resolver: zodResolver(dateRangeSchema),
    defaultValues: {
      dateType: defaultValues?.dateType || 'thisWeek',
      dateRange: defaultValues?.dateRange,
    },
  });

  // Watch form values for UI updates
  const dateType = form.watch('dateType');
  const dateRange = form.watch('dateRange');

  // Handler for form submission
  const handleSubmit = React.useCallback(
    (data: DateRangeFormData) => {
      onApply(data);
    },
    [onApply],
  );

  // Handler for preset option selection
  const handleOptionClick = React.useCallback(
    (value: typeof dateTypes[number]) => {
      form.setValue('dateType', value);

        // Calculate date range based on selected preset
        const { startDate, endDate } = calculateDatesFromType(value);

        // For allTime, set dateRange to undefined
        if (value === 'allTime') {
          form.setValue('dateRange', undefined);
        } else {
          const newRange: DateRangeFormData['dateRange'] = {
            from: startDate?.toISOString().split('T')[0],
            to: endDate?.toISOString().split('T')[0],
          };
          form.setValue('dateRange', newRange);
        }

        handleSubmit(form.getValues());
        setOpen(false);
      },
      [form, handleSubmit],
  );

  // Handler for calendar date selection
  const handleDateRangeSelect = React.useCallback(
    (range: DateRange | undefined, selectedDay?: Date) => {
      if (!range) {
        form.setValue('dateRange', undefined);
        return;
      }

      // If a full range already exists and the user starts a new selection,
      // reset so that first click becomes the new `from`
      const currentRange = form.getValues('dateRange');
      const hasCompleteRange = Boolean(currentRange?.from && currentRange?.to);
      const nextRange = selectedDay && hasCompleteRange
        ? { from: selectedDay, to: undefined }
        : range;
      const newRange: DateRangeFormData['dateRange'] = {
        from: nextRange?.from ? format(nextRange.from, 'yyyy-MM-dd') : undefined,
        to: nextRange?.to ? format(nextRange.to, 'yyyy-MM-dd') : undefined,
      };
      // Set type to custom and update date range
      form.setValue('dateType', 'custom');
      form.setValue('dateRange', newRange);

      // Submit if both dates are selected
      if (newRange.from && newRange.to) {
        handleSubmit(form.getValues());
        setCalendarOpen(false);
      }
    },
    [form, handleSubmit, setCalendarOpen],
  );

  // Handler for calendar dropdown state
  const handleCalendarOpenChange = React.useCallback(
    (isOpen: boolean) => {
      setCalendarOpen(isOpen);
      // Close main dropdown when calendar is closed with valid selection
      if (!isOpen && dateRange?.from && dateRange?.to) {
        setOpen(false);
      }
    },
    [dateRange],
  );

  return {
    open,
    setOpen,
    calendarOpen,
    dateType,
    dateRange,
    handleOptionClick,
    handleDateRangeSelect,
    handleCalendarOpenChange,
  };
}
